/**
 * Overview — "is the app working?" in one card, the three steps of testing,
 * what needs the designer's attention, and how much of the specification the
 * cases cover.
 */

import { useEffect, useState, type ReactNode } from 'react'
import toast from 'react-hot-toast'
import { Download, Play, RotateCcw, Sparkles } from 'lucide-react'
import Button from '@/components/common/Button'
import {
  apiError, buildReport, runFileUrl,
  type Coverage, type Overview, type RunDetail,
} from '@/api/uiTesting'
import type { TestingNav } from './TestingPage'
import { isLive } from './TestingPage'
import {
  Badge, Dot, IssuesTable, NumberDot, ShareBar, TONE, ago, h2Style, mutedText, panel, took, type Tone,
} from './shared'
import { nice } from './plainSteps'

interface Props {
  project: string
  overview: Overview | null
  coverage: Coverage | null
  latestRun: RunDetail | null
  canTest: boolean
  nav: TestingNav
  onSeed: () => Promise<void> | void
  seeding: boolean
  onReload: () => Promise<void> | void
}

const POLL_MS = 3000

export default function OverviewTab({
  project, overview, coverage, latestRun, canTest, nav, onSeed, seeding, onReload,
}: Props) {
  // Keep the health card current while a run is going.
  const live = isLive(latestRun?.status)
  useEffect(() => {
    if (!live) return
    const t = window.setInterval(() => { void onReload() }, POLL_MS)
    return () => window.clearInterval(t)
  }, [live, onReload])

  if (!overview) return <div style={mutedText}>The overview could not be loaded.</div>

  const open = overview.defects?.open ?? 0
  const kept = overview.test_data?.kept ?? 0
  const uncoveredSections = coverage?.uncovered_sections ?? []
  const uncoveredRules = coverage?.uncovered_rules ?? []
  const lastRun = overview.runs[0]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <Health project={project} overview={overview} run={latestRun} canTest={canTest} nav={nav}
              onSeed={onSeed} seeding={seeding} />

      {overview.unreadable_cases?.length > 0 && (
        <section style={{ ...panel, padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h2 style={h2Style}>{overview.unreadable_cases.length} case file(s) can’t be read</h2>
          <IssuesTable issues={overview.unreadable_cases} />
        </section>
      )}

      <section aria-labelledby="tw-steps" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h2 id="tw-steps" style={{ ...h2Style, fontSize: 17, color: 'var(--color-text-secondary)' }}>
          How testing works here
        </h2>
        <ol style={{
          listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        }}>
          <StepCard n={1} tone={overview.cases ? 'ok' : 'warn'} title="Test cases"
                    state={overview.cases ? `Ready · ${overview.cases} cases` : 'None yet'}
                    body="Made from your requirements and screens. Baseline cases are generated; Ask AI adds the business scenarios."
                    cta="Review cases" onClick={() => nav.go('cases')} />
          <StepCard n={2} tone="info" title="Run in a browser"
                    state={lastRun ? `Last run ${ago(lastRun.started_at)}` : 'Not run yet'}
                    body="Each case opens the app like a user, step by step, and saves a screenshot of every step as evidence."
                    cta={lastRun ? 'Open last run' : 'Open runs'}
                    onClick={() => nav.go('runs', lastRun ? { run: lastRun.run_id } : {})} />
          <StepCard n={3} tone={open ? 'err' : 'ok'} title="Decide on bugs"
                    state={open ? `${open} waiting for you` : 'Nothing waiting'}
                    body="Send real bugs to Support, close the ones that are not bugs, and download the signed-off report."
                    cta="Go to bugs" onClick={() => nav.go('bugs')} />
        </ol>
      </section>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        <section aria-labelledby="tw-attn" style={{
          ...panel, flex: '999 1 520px', minWidth: 0, padding: 24, display: 'flex', flexDirection: 'column',
        }}>
          <h2 id="tw-attn" style={{ ...h2Style, marginBottom: 10 }}>Needs your attention</h2>
          {open === 0 && uncoveredSections.length + uncoveredRules.length === 0 && kept === 0 && (
            <div style={{ ...mutedText, fontSize: 15, padding: '14px 0', borderTop: '1px solid var(--color-border)' }}>
              Nothing needs you right now.
            </div>
          )}
          {open > 0 && (
            <Todo num={open} tone="err" title={`${open} new bug${open === 1 ? '' : 's'} need${open === 1 ? 's' : ''} a decision`}
                  body="Found by test runs and not in Support yet — send them on, or close the ones that are not bugs."
                  cta="Review bugs" onClick={() => nav.go('bugs')} />
          )}
          {uncoveredSections.length + uncoveredRules.length > 0 && (
            <Todo num={uncoveredSections.length + uncoveredRules.length} tone="warn"
                  title={untestedTitle(uncoveredSections.length, uncoveredRules.length)}
                  body={`${uncoveredSections.slice(0, 3).map((s) => s.title).join(' · ')
                    || uncoveredRules.slice(0, 2).map((r) => r.section).join(' · ')}. Ask AI can write cases for them from the specification.`}
                  cta="Write tests"
                  onClick={() => nav.go('cases', {
                    requirement: uncoveredSections[0]?.key ?? uncoveredRules[0]?.section_key ?? null,
                  })} />
          )}
          {kept > 0 && (
            <Todo num={kept} tone="info" title={`${kept} test record${kept === 1 ? ' is' : 's are'} being kept`}
                  body="Created by test runs so you can look at them. Clean them up when you no longer need them."
                  cta="Review data" onClick={() => nav.go('data')} />
          )}
        </section>

        <CoverageCard coverage={coverage} onOpen={(key) => nav.go('cases', { requirement: key })} />
      </div>

      <section aria-labelledby="tw-data" style={{
        ...panel, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 420px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h2 id="tw-data" style={{ ...h2Style, fontSize: 17 }}>Test data</h2>
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 15 }}>
            {kept
              ? `${kept} record${kept === 1 ? '' : 's'} created by tests ${kept === 1 ? 'is' : 'are'} kept in the preview database so you can look at them. Records you changed since are never removed.`
              : 'No test records are being kept.'}
          </span>
        </div>
        <Button variant="secondary" onClick={() => nav.go('data')}>
          {kept ? 'Clean up test data…' : 'Open test data'}
        </Button>
      </section>
    </div>
  )
}

function untestedTitle(sections: number, rules: number): string {
  const parts = []
  if (sections) parts.push(`${sections} requirement${sections === 1 ? '' : 's'}`)
  if (rules) parts.push(`${rules} business rule${rules === 1 ? '' : 's'}`)
  return `${parts.join(' and ')} ${sections + rules === 1 ? 'has' : 'have'} no test`
}

// ── the health card ────────────────────────────────────────────────────────

function Health({ project, overview, run, canTest, nav, onSeed, seeding }: {
  project: string; overview: Overview; run: RunDetail | null; canTest: boolean; nav: TestingNav
  onSeed: () => Promise<void> | void; seeding: boolean
}) {
  const [reporting, setReporting] = useState(false)
  const wrap = (side: ReactNode, body: ReactNode) => (
    <section aria-labelledby="tw-health" style={{
      ...panel, padding: 28, display: 'flex', flexWrap: 'wrap', gap: 32, alignItems: 'center',
    }}>
      <div style={{ flex: '999 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>{body}</div>
      <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 10 }}>{side}</div>
    </section>
  )
  const headline = (text: string) => (
    <h2 id="tw-health" style={{ margin: 0, fontSize: 29, fontWeight: 600, lineHeight: 1.25 }}>{text}</h2>
  )
  const sub = (text: ReactNode) => (
    <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 16, lineHeight: 1.55, maxWidth: 680 }}>{text}</p>
  )

  if (!overview.cases) {
    return wrap(
      canTest && (
        <>
          <Button loading={seeding} icon={<Sparkles size={15} />} onClick={() => void onSeed()}>Generate test cases</Button>
          <Button variant="ghost" onClick={() => nav.askAi()}>Ask AI to write them</Button>
        </>
      ),
      <>
        <Badge tone="neutral">Not tested yet</Badge>
        {headline('This application has no test cases yet.')}
        {sub('Generate the baseline cases from its screens — every screen opens, saves and refuses bad input — then let Ask AI add the business scenarios from the requirements.')}
      </>,
    )
  }

  if (!run) {
    return wrap(
      canTest && <Button icon={<Play size={15} />} onClick={() => void nav.runCases()}>Run all tests</Button>,
      <>
        <Badge tone="neutral">Not run yet</Badge>
        {headline(`${overview.cases} test cases are ready.`)}
        {sub('A run opens the application in a real browser, follows every case step by step and keeps a screenshot of each step.')}
      </>,
    )
  }

  const c = run.counts || {}
  const passed = c.passed ?? 0
  const failed = c.failed ?? 0
  const blocked = c.blocked ?? 0
  const total = run.total ?? passed + failed + blocked

  if (isLive(run.status)) {
    const done = run.done ?? 0
    return wrap(
      <Button onClick={() => nav.go('runs', { run: run.run_id })}>Watch the run</Button>,
      <>
        <Badge tone="info">Running</Badge>
        {headline(`Testing… ${done} of ${total} checks done.`)}
        <ShareBar label={`${passed} passed, ${failed} failed, ${blocked} could not run`} parts={[
          { value: passed, tone: 'ok' }, { value: failed, tone: 'err' }, { value: blocked, tone: 'warn' },
          { value: Math.max(0, total - done), tone: 'neutral' },
        ]} />
        <Legend passed={passed} failed={failed} blocked={blocked} />
      </>,
    )
  }

  const failedCases = (run.cases || []).filter((x) => x.status === 'failed')
  const failedObjects = Array.from(new Set(failedCases.map((x) => x.object).filter(Boolean) as string[]))
  const where = failedObjects.length
    ? ` on ${failedObjects.slice(0, 3).map((o) => `the ${nice(o, true)}`).join(', ')}`
      + (failedObjects.length > 3 ? ` and ${failedObjects.length - 3} more` : '')
    : ''
  const status: { tone: Tone; label: string } = run.error ? { tone: 'err', label: 'The run failed' }
    : failed === 0 && blocked === 0 ? { tone: 'ok', label: 'All working' }
      : failed === 0 ? { tone: 'warn', label: 'Some checks couldn’t run' }
        : passed / Math.max(1, total) >= 0.8 ? { tone: 'warn', label: 'Mostly working' }
          : { tone: 'err', label: 'Needs work' }
  const meta = [`Last run ${ago(run.started_at)}`, took(run.started_at, run.finished_at),
    run.started_by ? `by ${run.started_by}` : ''].filter(Boolean).join(' · ')
  const retry = [...failedCases, ...(run.cases || []).filter((x) => x.status === 'blocked')].map((x) => x.id)

  const docx = run.report?.docx
  const onReport = async () => {
    if (docx) { window.open(runFileUrl(project, run.run_id, docx), '_blank'); return }
    setReporting(true)
    try {
      const r = await buildReport(project, run.run_id)
      window.open(runFileUrl(project, run.run_id, r.docx.split('/').pop() || r.docx), '_blank')
    } catch (err) {
      toast.error(apiError(err, 'Could not build the test report'))
    } finally {
      setReporting(false)
    }
  }

  return wrap(
    <>
      <Button style={{ width: '100%' }} onClick={() => nav.go('runs', { run: run.run_id })}>
        {failed || blocked ? 'See what failed' : 'Open the run'}
      </Button>
      {(docx || canTest) && (
        <Button variant="secondary" style={{ width: '100%' }} icon={<Download size={15} />} loading={reporting}
                onClick={() => void onReport()}>Download test report (.docx)</Button>
      )}
      {canTest && retry.length > 0 && (
        <Button variant="ghost" style={{ width: '100%' }} icon={<RotateCcw size={15} />}
                onClick={() => void nav.runCases(retry)}>Run only the failed ones again</Button>
      )}
    </>,
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Badge tone={status.tone}><Dot tone={status.tone} size={8} />{status.label}</Badge>
        <span style={{ ...mutedText, fontSize: 14 }}>{meta}</span>
      </div>
      {headline(run.error
        ? 'The run stopped before it could finish.'
        : `${passed} of ${total} checks passed.${failed ? ` ${failed} failed${where}.` : ''}`)}
      {run.error ? sub(run.error)
        : blocked > 0 ? sub(`${blocked} check${blocked === 1 ? '' : 's'} couldn’t run — usually a screen the test could not reach, not a bug in the application.`)
          : failed === 0 ? sub('Every check did what it should.') : null}
      <ShareBar label={`${passed} passed, ${failed} failed, ${blocked} could not run`} height={12} parts={[
        { value: passed, tone: 'ok' }, { value: failed, tone: 'err' }, { value: blocked, tone: 'warn' },
      ]} />
      <Legend passed={passed} failed={failed} blocked={blocked} />
    </>,
  )
}

function Legend({ passed, failed, blocked }: { passed: number; failed: number; blocked: number }) {
  const item = (tone: Tone, text: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: TONE[tone].fg }} />{text}
    </span>
  )
  return (
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 14, color: 'var(--color-text-secondary)' }}>
      {item('ok', `${passed} passed`)}
      {item('err', `${failed} failed`)}
      {item('warn', `${blocked} couldn’t run`)}
    </div>
  )
}

// ── smaller cards ──────────────────────────────────────────────────────────

function StepCard({ n, tone, title, state, body, cta, onClick }: {
  n: number; tone: Tone; title: string; state: string; body: string; cta: string; onClick: () => void
}) {
  return (
    <li style={{ ...panel, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <NumberDot n={n} tone={tone} size={32} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <strong style={{ fontSize: 16 }}>{title}</strong>
          <span style={{ color: TONE[tone].fg, fontSize: 14 }}>{state}</span>
        </div>
      </div>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.5, fontSize: 15, flex: 1 }}>{body}</p>
      <Button variant="secondary" style={{ alignSelf: 'flex-start' }} onClick={onClick}>{cta}</Button>
    </li>
  )
}

function Todo({ num, tone, title, body, cta, onClick }: {
  num: number; tone: Tone; title: string; body: string; cta: string; onClick: () => void
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', flexWrap: 'wrap',
      borderTop: '1px solid var(--color-border)',
    }}>
      <span style={{
        width: 40, height: 40, borderRadius: 10, flex: 'none', display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', fontWeight: 700, fontSize: 16, background: TONE[tone].bg, color: TONE[tone].fg,
      }}>{num}</span>
      <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <strong style={{ fontSize: 16 }}>{title}</strong>
        <span style={{ color: 'var(--color-text-secondary)', lineHeight: 1.45, fontSize: 15 }}>{body}</span>
      </div>
      <Button variant="secondary" onClick={onClick}>{cta}</Button>
    </div>
  )
}

function CoverageCard({ coverage, onOpen }: { coverage: Coverage | null; onOpen: (key: string | null) => void }) {
  const box = (children: ReactNode) => (
    <section aria-labelledby="tw-cov" style={{
      ...panel, flex: '1 1 340px', minWidth: 0, padding: 24, display: 'flex', flexDirection: 'column', gap: 16,
    }}>{children}</section>
  )
  if (!coverage || !coverage.frs_found) {
    return box(
      <>
        <h2 id="tw-cov" style={h2Style}>Requirements covered</h2>
        <span style={{ ...mutedText, fontSize: 15 }}>
          This application has no requirement specification, so cases are made from its screens instead.
        </span>
      </>,
    )
  }
  const modules = new Map<string, { total: number; covered: number }>()
  for (const s of coverage.matrix) {
    const m = modules.get(s.module || 'Other') || { total: 0, covered: 0 }
    m.total += 1
    if (s.cases.length) m.covered += 1
    modules.set(s.module || 'Other', m)
  }
  const missing = coverage.sections - coverage.sections_covered
  return box(
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <h2 id="tw-cov" style={h2Style}>Requirements covered</h2>
        <span style={{ fontSize: 23, fontWeight: 600 }}>
          {coverage.sections_covered}
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 16 }}> / {coverage.sections}</span>
        </span>
      </div>
      {Array.from(modules.entries()).map(([name, m]) => {
        const pct = m.total ? Math.round((m.covered / m.total) * 100) : 0
        const tone: Tone = pct === 100 ? 'ok' : pct >= 50 ? 'warn' : 'err'
        return (
          <div key={name} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, gap: 8 }}>
              <span>{name}</span>
              <span style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{m.covered} of {m.total}</span>
            </div>
            <ShareBar height={8} label={`${name}: ${m.covered} of ${m.total} covered`}
                      parts={[{ value: m.covered, tone }, { value: m.total - m.covered, tone: 'neutral' }]} />
          </div>
        )
      })}
      <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
        {coverage.rules_covered} of {coverage.rules} business rules have a test.
      </div>
      {missing > 0 && (
        <button type="button" onClick={() => onOpen(coverage.uncovered_sections[0]?.key ?? null)}
                style={{ alignSelf: 'flex-start', fontSize: 14, color: 'var(--color-accent)', padding: 0 }}>
          See the {missing} requirement{missing === 1 ? '' : 's'} with no test →
        </button>
      )}
    </>,
  )
}
